import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;
const TOKEN_KEY = "tx_token";

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const setAuthToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    if (err.response?.status === 401 && !url.startsWith("/auth/")) {
      setAuthToken(null);
      if (window.location.pathname !== "/login") window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const downloadRunPdf = async (runId) => {
  const r = await api.get(`/runs/${runId}/export.pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `transactr-trace-${runId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return r.headers["x-seal-id"];
};

export const endpoints = {
  integrations: () => api.get("/integrations").then((r) => r.data),
  awsStatus: () => api.get("/integrations/aws/status").then((r) => r.data),
  opportunities: () => api.get("/salesforce/opportunities").then((r) => r.data),
  partners: () => api.get("/aws/ace/partners").then((r) => r.data),
  cpRegistration: () => api.get("/aws/cp-registration").then((r) => r.data),
  expiringSubs: () => api.get("/aws/expiring-subscriptions").then((r) => r.data),

  resolveBuyer: (aws_account_id) =>
    api.post("/buyer/resolve", { aws_account_id }).then((r) => r.data),

  authorizations: () => api.get("/authorizations").then((r) => r.data),
  getAuthorization: (id) => api.get(`/authorizations/${id}`).then((r) => r.data),
  createAuthorization: (spec) => api.post("/authorizations", spec).then((r) => r.data),
  validateAuthorization: (id) => api.post(`/authorizations/${id}/validate`).then((r) => r.data),
  publishAuthorization: (id, confirmation_text) =>
    api.post(`/authorizations/${id}/publish`, { confirmation_text }).then((r) => r.data),
  ammpStatus: (id) => api.get(`/authorizations/${id}/ammp-status`).then((r) => r.data),

  offers: () => api.get("/offers").then((r) => r.data),
  releaseOffer: (id) => api.post(`/offers/${id}/release`).then((r) => r.data),
  transitionOffer: (id, state) =>
    api.post(`/offers/${id}/transition`, { state }).then((r) => r.data),

  runs: () => api.get("/runs").then((r) => r.data),
  getRun: (id) => api.get(`/runs/${id}`).then((r) => r.data),
  startRun: (intent, opts = {}) =>
    api.post("/runs", { intent, ...opts }).then((r) => r.data),
  approveRun: (id) => api.post(`/runs/${id}/approve`).then((r) => r.data),
  rejectRun: (id) => api.post(`/runs/${id}/reject`).then((r) => r.data),
  streamRunUrl: (id) => `${API}/runs/${id}/stream?token=${encodeURIComponent(getAuthToken() || "")}`,

  seals: () => api.get("/trace/seals").then((r) => r.data),
  verifySeal: (seal, sig) => api.get("/trace/verify", { params: { seal, sig } }).then((r) => r.data),

  summary: () => api.get("/dashboard/summary").then((r) => r.data),

  rateCards: () => api.get("/rate-cards").then((r) => r.data),
  createRateCard: (rc) => api.post("/rate-cards", rc).then((r) => r.data),
  updateRateCard: (id, patch) => api.patch(`/rate-cards/${id}`, patch).then((r) => r.data),
  deleteRateCard: (id) => api.delete(`/rate-cards/${id}`).then((r) => r.data),

  sfStatus: () => api.get("/integrations/salesforce/status").then((r) => r.data),
  sfConnect: () => api.get("/integrations/salesforce/connect").then((r) => r.data),
  sfDisconnect: () => api.post("/integrations/salesforce/disconnect").then((r) => r.data),

  workspace: () => api.get("/workspace").then((r) => r.data),
  renameWorkspace: (name) => api.patch("/workspace", { name }).then((r) => r.data),
  addMember: (payload) => api.post("/workspace/members", payload).then((r) => r.data),
  removeMember: (id) => api.delete(`/workspace/members/${id}`).then((r) => r.data),

  apiKeys: () => api.get("/workspace/api-keys").then((r) => r.data),
  createApiKey: (name) => api.post("/workspace/api-keys", { name }).then((r) => r.data),
  revokeApiKey: (id) => api.delete(`/workspace/api-keys/${id}`).then((r) => r.data),
  revealApiKey: (id) => api.post(`/workspace/api-keys/${id}/reveal`).then((r) => r.data),
  webhook: () => api.get("/workspace/webhook").then((r) => r.data),
  saveWebhook: (cfg) => api.put("/workspace/webhook", cfg).then((r) => r.data),
  rotateWebhookSecret: () => api.post("/workspace/webhook/rotate-secret").then((r) => r.data),
  testWebhook: () => api.post("/workspace/webhook/test").then((r) => r.data),
  webhookDeliveries: () => api.get("/workspace/webhook/deliveries").then((r) => r.data),

  handoffDeals: () => api.get("/handoff/deals").then((r) => r.data),
  pushHandoffDeal: (deal) => api.post("/handoff/deals", deal).then((r) => r.data),
  dispatchDeal: (id) => api.post(`/handoff/deals/${id}/dispatch`).then((r) => r.data),
  dealTimeline: (id) => api.get(`/handoff/deals/${id}/timeline`).then((r) => r.data),

  playbooks: () => api.get("/incentive-playbooks").then((r) => r.data),
  createPlaybook: (pb) => api.post("/incentive-playbooks", pb).then((r) => r.data),
  updatePlaybook: (id, patch) => api.patch(`/incentive-playbooks/${id}`, patch).then((r) => r.data),
  deletePlaybook: (id) => api.delete(`/incentive-playbooks/${id}`).then((r) => r.data),
  matchPlaybook: (q) => api.post("/incentive-playbooks/match", q).then((r) => r.data),
  playbookAnalytics: () => api.get("/incentive-playbooks/analytics").then((r) => r.data),

  ssoConfig: () => api.get("/sso/config").then((r) => r.data),
  saveSsoConfig: (cfg) => api.put("/sso/config", cfg).then((r) => r.data),
  rotateSsoSecret: () => api.post("/sso/rotate-secret").then((r) => r.data),
  revealSsoSecret: () => api.post("/sso/reveal-secret").then((r) => r.data),

  notifications: () => api.get("/notifications").then((r) => r.data),
  markRead: (id) => api.post(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => api.post("/notifications/read-all").then((r) => r.data),
};

export const fmtUSD = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);

export const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};
